import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const VEHICLE_TYPE_OPTIONS = ["2 Wheeler", "Hydra", "Truck", "Tempo", "Forklifter"];
const OWNER_TYPE_OPTIONS = ["Personal", "Company Owned"];

export default function MeterLogForm({ form, setField, vehicles }) {
  const selectedVehicle = vehicles.find((v) => v.name === form.vehicle_no);
  const mappedOwner = selectedVehicle?.category === "Employee" ? "Personal" : "Company Owned";
  const ownerType = form.owner_type || mappedOwner || "Company Owned";

  const odoOut = parseFloat(form.odometer_out) || 0;
  const odoIn = parseFloat(form.odometer_in) || 0;
  const kmRun = Math.max(0, odoIn - odoOut).toFixed(1);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Vehicle No. *</Label>
          <Select value={form.vehicle_no || ""} onValueChange={(v) => {
            const veh = vehicles.find((x) => x.name === v);
            setField("vehicle_no", v);
            setField("vehicle_name", veh?.details?.vehicle_type || "");
            setField("vehicle_type", veh?.details?.vehicle_type || form.vehicle_type || "");
            setField("owner_type", veh?.category === "Employee" ? "Personal" : "Company Owned");
            setField("employee_name", veh?.details?.employee_name || "");
          }}>
            <SelectTrigger className="h-11" data-testid="field-meter-vehicle">
              <SelectValue placeholder="Select vehicle" />
            </SelectTrigger>
            <SelectContent>
              {vehicles.map((v) => (
                <SelectItem key={v.id} value={v.name}>
                  {v.name}{v.details?.vehicle_type ? ` — ${v.details.vehicle_type}` : ""}{v.category === "Employee" ? " (Personal)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Vehicle Type *</Label>
          <Select value={form.vehicle_type || ""} onValueChange={(v) => setField("vehicle_type", v)}>
            <SelectTrigger className="h-11" data-testid="field-meter-vehicle-type">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {VEHICLE_TYPE_OPTIONS.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Owner Type *</Label>
          <Select value={ownerType} onValueChange={(v) => setField("owner_type", v)}>
            <SelectTrigger className="h-11" data-testid="field-meter-owner-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OWNER_TYPE_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        {ownerType === "Personal" && (
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Employee Name</Label>
            <Input value={form.employee_name || ""} onChange={(e) => setField("employee_name", e.target.value)} className="h-11" data-testid="field-meter-employee" />
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Driver Name</Label>
          <Input value={form.driver_name || ""} onChange={(e) => setField("driver_name", e.target.value)} className="h-11" data-testid="field-meter-driver" />
        </div>
      </div>

      <div className="border rounded-lg p-4 bg-slate-50/50 space-y-3">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-600">Odometer Readings</Label>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-[11px] text-gray-500">Out Reading (Km) *</Label>
            <Input type="number" step="0.1" value={form.odometer_out || ""} onChange={(e) => setField("odometer_out", e.target.value)} className="h-10" placeholder="e.g. 45230" data-testid="field-odo-out" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] text-gray-500">In Reading (Km) <span className="text-amber-600">— optional</span></Label>
            <Input type="number" step="0.1" value={form.odometer_in || ""} onChange={(e) => setField("odometer_in", e.target.value)} className="h-10" placeholder="Fill on return" data-testid="field-odo-in" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] text-gray-500">Km Run</Label>
            <div className="h-10 flex items-center px-3 bg-emerald-50 border border-emerald-200 rounded text-sm font-bold text-emerald-700" data-testid="km-run-display">
              {kmRun} km
            </div>
          </div>
        </div>
        <p className="text-[10px] text-gray-400 italic">Leave the In Reading blank if the vehicle has not returned yet. The entry will be saved as <strong>OPEN</strong>; use the &ldquo;Close Trip&rdquo; button once the vehicle is back to record return reading and auto-calculate Km Run.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Purpose</Label>
          <Input value={form.purpose || ""} onChange={(e) => setField("purpose", e.target.value)} className="h-11" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Remarks</Label>
          <Input value={form.remarks || ""} onChange={(e) => setField("remarks", e.target.value)} className="h-11" />
        </div>
      </div>
    </div>
  );
}
